import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldOff, XCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Public 1-click unsubscribe landing page.
 * Reached via a signed token in the mail footer (and the auto-POSTed
 * `List-Unsubscribe` URL from Gmail/Outlook's native unsubscribe button).
 *
 * Auto-confirms on mount — no second click required, which is what makes
 * this RFC 8058 "one-click" compliant. We still show the result + a manual
 * retry button in case the auto-POST fails.
 */
export default function Unsubscribe() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [state, setState] = useState("loading"); // loading | confirming | done | error | already
    const [error, setError] = useState(null);

    const confirm = useCallback(async () => {
        setState("confirming");
        try {
            await api.post(`/unsubscribe/${token}`);
            setState("done");
        } catch (e) {
            setError(e.response?.data?.detail || "Uitschrijven mislukt — probeer opnieuw.");
            setState("error");
        }
    }, [token]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/unsubscribe/${token}`);
                if (cancelled) return;
                setInfo(data);
                if (data.already_unsubscribed) {
                    setState("already");
                } else {
                    // Auto-confirm — RFC 8058 one-click behaviour.
                    confirm();
                }
            } catch (e) {
                if (cancelled) return;
                setError(e.response?.data?.detail || "Deze link is ongeldig of verlopen.");
                setState("error");
            }
        })();
        return () => { cancelled = true; };
    }, [token, confirm]);

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-md bg-[#FFFFFF] border border-zinc-200 rounded-md p-8 lg:p-10" data-testid="unsubscribe-card">
                <div className="flex items-center gap-3 mb-6">
                    <img
                        src="https://customer-assets.emergentagent.com/job_makelaar-booking/artifacts/c00a1vjs_Logo%20Social%20Factory%20werkbestand_Tekengebied%201-02.png"
                        alt="Social Factory"
                        className="h-10 w-10 object-contain invert"
                    />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E5A93D] font-bold">Social Factory</div>
                        <div className="text-xs text-zinc-500">Vastgoed Studio Pro</div>
                    </div>
                </div>

                {state === "loading" && (
                    <div className="text-center py-10">
                        <Loader2 className="h-8 w-8 mx-auto text-zinc-500 animate-spin mb-3" />
                        <p className="text-sm text-zinc-500">Link controleren…</p>
                    </div>
                )}

                {state === "confirming" && (
                    <div className="text-center py-6" data-testid="unsubscribe-confirming">
                        <Loader2 className="h-8 w-8 mx-auto text-[#E5A93D] animate-spin mb-3" />
                        <p className="text-sm text-zinc-600">Bezig met uitschrijven…</p>
                        {info?.email && <p className="mt-1 text-xs text-zinc-500">{info.email}</p>}
                    </div>
                )}

                {state === "done" && (
                    <div className="text-center py-4" data-testid="unsubscribe-done">
                        <div className="h-14 w-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-5">
                            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
                        </div>
                        <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Uitgeschreven</h1>
                        <p className="text-sm text-zinc-500 leading-relaxed mb-5">
                            We hebben <strong className="text-zinc-950">{info?.email}</strong> verwijderd uit onze mailinglijst.
                            U ontvangt geen verdere outreach-mails meer van Social Factory.
                        </p>
                        <p className="text-[11px] text-zinc-500">
                            Per ongeluk geklikt? Stuur een mail naar
                            {" "}<a href="mailto:info@social-factory.be" className="text-[#E5A93D] hover:underline">info@social-factory.be</a>.
                        </p>
                    </div>
                )}

                {state === "already" && (
                    <div className="text-center py-4" data-testid="unsubscribe-already">
                        <div className="h-14 w-14 mx-auto rounded-full bg-zinc-200 border border-zinc-300 flex items-center justify-center mb-5">
                            <ShieldOff className="h-7 w-7 text-zinc-600" />
                        </div>
                        <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Al uitgeschreven</h1>
                        <p className="text-sm text-zinc-500 leading-relaxed">
                            <strong className="text-zinc-950">{info?.email}</strong> staat al op onze opt-out lijst. Geen actie nodig.
                        </p>
                    </div>
                )}

                {state === "error" && (
                    <div className="text-center py-4" data-testid="unsubscribe-error">
                        <div className="h-14 w-14 mx-auto rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mb-5">
                            <XCircle className="h-7 w-7 text-rose-700" />
                        </div>
                        <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Niet gelukt</h1>
                        <p className="text-sm text-zinc-500 leading-relaxed mb-5">{error}</p>
                        <button
                            type="button"
                            onClick={confirm}
                            data-testid="unsubscribe-retry"
                            className="inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm transition active:scale-[0.98]"
                        >
                            Opnieuw proberen
                        </button>
                    </div>
                )}

                <div className="mt-8 pt-5 border-t border-zinc-200 text-center">
                    <Link to="/" className="text-xs text-zinc-500 hover:text-[#E5A93D] transition">
                        ← terug naar social-factory.be
                    </Link>
                </div>
            </div>
        </div>
    );
}
